/**
 * Affiliate Partner Program — the single place the referral markers are written and read.
 *
 * Created 2026-09-22. Before this, the same read logic was duplicated privately inside
 * AuthService and the keys were string literals in AffiliateRedirectComponent; social login
 * needed a third copy, which is exactly how the two would have drifted apart.
 *
 * Three markers, written by AffiliateRedirectComponent (/r/:affiliateCode):
 *  - ll_ref_token  — the server-recorded click token. Authoritative: the backend checks its own
 *                    clickedAt against the attribution window, so a stale token is rejected
 *                    server-side no matter what the browser kept. Stored as BOTH a cookie and a
 *                    localStorage entry, because each survives a different kind of clearing.
 *  - ll_ref_code   — the bare affiliate code, the fallback for when the click POST never landed
 *                    (offline, rate-limited, program switched off). The backend CANNOT window-check
 *                    this one — there is no recorded click to date it — so the expiry below is the
 *                    only thing stopping a year-old code from still paying a commission.
 *  - ll_ref_name   — the affiliate's display name, purely for the "You were referred by X" banner.
 */

export const REF_TOKEN_KEY = 'll_ref_token';
export const REF_CODE_KEY = 'll_ref_code';
export const REF_NAME_KEY = 'll_ref_name';
export const REF_SAVED_AT_KEY = 'll_ref_saved_at';

/** Matches the backend's default attributionWindowDays (AffiliateProgramSettings) and the
 *  ll_ref_token cookie's own max-age. Kept as one named constant rather than a third bare 30. */
export const REF_WINDOW_DAYS = 30;

const REF_WINDOW_MS = REF_WINDOW_DAYS * 24 * 60 * 60 * 1000;

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // private browsing / storage blocked
    return null;
  }
}

function readCookie(key: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + key + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/** True once the stored markers are older than the attribution window. Missing/unparseable
 *  timestamp is treated as expired for the CODE only (see readRefCode) — markers written before
 *  this timestamp existed are, by definition, already old. */
function isExpired(): boolean {
  const savedAt = readLocal(REF_SAVED_AT_KEY);
  if (!savedAt) {
    return true;
  }
  const parsed = Number(savedAt);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return true;
  }
  return Date.now() - parsed > REF_WINDOW_MS;
}

/** Writes all three markers plus the timestamp. Called only by AffiliateRedirectComponent. */
export function saveRef(code: string, clickToken: string | null, affiliateName: string | null): void {
  try {
    localStorage.setItem(REF_CODE_KEY, code);
    localStorage.setItem(REF_SAVED_AT_KEY, String(Date.now()));
    if (clickToken) {
      localStorage.setItem(REF_TOKEN_KEY, clickToken);
    }
    if (affiliateName) {
      localStorage.setItem(REF_NAME_KEY, affiliateName);
    }
  } catch {
    // storage blocked — the cookie below is the fallback for the token
  }
  if (clickToken) {
    const maxAgeSeconds = REF_WINDOW_DAYS * 24 * 60 * 60;
    document.cookie =
      `${REF_TOKEN_KEY}=${encodeURIComponent(clickToken)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
  }
}

/**
 * The click token. Cookie first (it is the copy that survives a localStorage clear), then
 * localStorage. Deliberately NOT expiry-checked here: the backend dates this token from its own
 * recorded click row and rejects it past the window, which is stricter and untamperable. Dropping
 * it client-side too would only hide a token the server was going to reject anyway.
 */
export function readRefToken(): string | null {
  return readCookie(REF_TOKEN_KEY) ?? readLocal(REF_TOKEN_KEY);
}

/**
 * The fallback code, dropped once past the attribution window. This is the client-side half of
 * the window: the backend has no recorded click to date this value against, so without this check
 * a code sitting in localStorage from months ago would still convert.
 */
export function readRefCode(): string | null {
  if (isExpired()) {
    // Only the code/name/timestamp are dropped — deliberately NOT the token. A browser that
    // clicked a link before ll_ref_saved_at existed has no timestamp and so reads as expired
    // here, but its click token is still perfectly valid and the backend will date it correctly;
    // clearing the token from this branch would destroy a working referral to fix a weaker one.
    // It also removes any dependence on the order these two readers happen to be called in.
    try {
      localStorage.removeItem(REF_CODE_KEY);
      localStorage.removeItem(REF_NAME_KEY);
      localStorage.removeItem(REF_SAVED_AT_KEY);
    } catch {
      // ignore
    }
    return null;
  }
  return readLocal(REF_CODE_KEY);
}

/** The referring affiliate's display name for the login-page banner, or null. Same expiry as the
 *  code — a banner naming a referrer whose code has already expired would be a lie. */
export function readRefName(): string | null {
  if (isExpired()) {
    return null;
  }
  return readLocal(REF_NAME_KEY);
}

/** Removes every marker. Safe to call at any time; never throws. */
export function clearRef(): void {
  try {
    localStorage.removeItem(REF_CODE_KEY);
    localStorage.removeItem(REF_NAME_KEY);
    localStorage.removeItem(REF_SAVED_AT_KEY);
    localStorage.removeItem(REF_TOKEN_KEY);
  } catch {
    // ignore
  }
  document.cookie = `${REF_TOKEN_KEY}=; path=/; max-age=0; SameSite=Lax`;
}
